// 📐 #6393 « / » 로 이어 붙은 제목 → 목록 장표(그리기 직전 · 저장 데이터는 그대로).
//
// ★왜★ 현장 브리핑 등 옛 설계도 가족의 연출 톤에는 «여러 항목 = 목록·카드» 배분 지시가 없어서, 디렉터가 예방 대책 세 개를
//   제목 한 칸에 «A / B / C» 로 이어 붙였다 → 큰 글자 5줄 덩어리(10-09 dev 유료 실측 장면 6). 연출 AI 지시문은 늘리지 않는다
//   (#6304 를 닫은 이유와 같다) — 그리는 쪽에서 결정적으로 나눈다.
// ★왜 다리(msb-blueprint)가 아니라 그리기 직전인가★
//   - 이미 만든 작업물·채팅으로 고친 제목도 같은 규칙을 받는다(다리는 첫 생성 한 번만 지난다).
//   - 저장된 장표는 그대로라 직접 고치기·정밀 편집·되돌리기가 원문 제목을 그대로 본다(«글자를 바꾸지 않는다»).
//   - 옛 4가족·모션 무대 3가족·VM 한쪽 패널이 모두 `Overlay169SlideLayer` 한 곳을 지난다.
// ★#6363(건설 예방대책 카드)과 겹치지 않는다★ — 그쪽은 브리핑 사실로 `preventionCard` 를 만든다. 여기는 `fullText` 만 본다
//   (같은 장면을 두 번 바꾸지 않는다 · 브리핑 사실 없이 만든 일반 설계도의 안전망).
import { briefLineCount } from "../../remotion/msb/motion-brief-shape";
import { T } from "../../remotion/msb/tokens";
import { safeInsetPx } from "../safe-zone";
import { SLIDE_ARRAY_CAPS } from "./slide-edit-contract";

/** 나누는 자리 — 앞뒤로 띄어 쓴 « / » 만(«1/2»·«상부/중간대» 같은 붙은 빗금은 항목 구분이 아니다). */
const SLASH = /[ \t]+\/[ \t]+/;

/** 이 줄 수 이상이면 «넘친다»(1920 기준 큰 제목 글자 · 글자 자리 전체 폭 · 폭 모델은 넉넉한 쪽). */
export const SLASH_LIST_MIN_LINES = 3;

/** 판정 폭(1920 기준 px) — 해상도와 무관하게 같은 결정을 내리도록 기준 화면 하나로 잰다(1080·480 받기가 갈리지 않게). */
const REF_W = (() => {
  const core = safeInsetPx("16:9", 1920, 1080, "core");
  return 1920 - core.left - core.right;
})();

type SlideLike = { type: string; content: Record<string, unknown> };

/** 제목을 « / » 로 나눈 항목(원문 조각 그대로 · 앞뒤 공백만 뗌). 2개 미만·빈 조각·상한 초과면 null. */
export function slashListItems(title: string): string[] | null {
  if (!SLASH.test(title)) return null;
  const items = title.split(SLASH).map((t) => t.trim());
  // 빈 조각·빗금으로 시작하거나 끝나는 조각(«A /  / B» 처럼 빗금이 겹친 꼴)은 항목 구분이 아니다 — 나누지 않는다.
  if (items.length < 2 || items.length > SLIDE_ARRAY_CAPS.items || items.some((t) => !t || /^\/|\/$/.test(t))) return null;
  return items;
}

/**
 * 큰 제목 칸(fullText)에 « / » 항목이 2개 이상 이어 붙었고 큰 글자로 `SLASH_LIST_MIN_LINES` 줄 이상이면 목록 장표로 바꿔 돌려준다.
 *   항목 = 제목 조각 그대로 · 목록 제목 = 부제(없으면 제목 없음) · 연출 키(anchor·revealMode·keyword·cardStyle…)는 그대로.
 *   해당 없으면 **같은 객체**를 돌려준다(종전 그리기 바이트 그대로).
 */
export function listifySlashTitle<S extends SlideLike>(slide: S): S;
export function listifySlashTitle<S extends SlideLike>(slide: S | null | undefined): S | null | undefined;
export function listifySlashTitle<S extends SlideLike>(slide: S | null | undefined): S | null | undefined {
  if (!slide || slide.type !== "fullText") return slide;
  const title = typeof slide.content?.title === "string" ? slide.content.title : "";
  const items = slashListItems(title);
  if (!items || briefLineCount(title, T.hero, REF_W) < SLASH_LIST_MIN_LINES) return slide;
  const { title: _t, sub, ...rest } = slide.content;
  const head = typeof sub === "string" && sub.trim() ? sub : undefined;
  return { ...slide, type: "checkList", content: { ...rest, ...(head ? { title: head } : {}), items } } as S;
}
